/**
 * @wharfkit/wallet-plugin-cleos v1.0.0
 * https://github.com/wharfkit/wallet-plugin-cleos
 *
 * @license
 * Copyright (c) 2023 Greymass Inc. All Rights Reserved.
 * 
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 * 
 * 1.  Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 * 
 * 2.  Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 * 
 * 3.  Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 * 
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR INTENDED FOR USE
 * IN THE DESIGN, CONSTRUCTION, OPERATION OR MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { AbstractWalletPlugin, WalletPluginMetadata, TransactHookTypes, Serializer } from '@wharfkit/session';

class WalletPluginCleos extends AbstractWalletPlugin {
    constructor() {
        super(...arguments);
        /**
         * The logic configuration for the wallet plugin.
         */
        this.config = {
            // Should the user interface display a chain selector?
            requiresChainSelect: true,
            // Should the user interface display a permission selector?
            requiresPermissionSelect: false,
            // Should the user interface require input for the permission?
            requiresPermissionEntry: true,
        };
        /**
         * The metadata for the wallet plugin to be displayed in the user interface.
         */
        this.metadata = WalletPluginMetadata.from({
            name: 'cleos',
            description: 'Copy and paste the transactions to sign in cleos.',
            logo: 'base_64_encoded_image',
            homepage: 'https://github.com/antelopeio/leap',
            download: 'https://github.com/antelopeio/leap',
        });
    }
    /**
     * A unique string identifier for this wallet plugin.
     *
     * It's recommended this is all lower case, no spaces, and only URL-friendly special characters (dashes, underscores, etc)
     */
    get id() {
        return 'wallet-plugin-cleos';
    }
    /**
     * Performs the wallet logic required to login and return the chain and permission level to use.
     *
     * @param options WalletPluginLoginOptions
     * @returns Promise<WalletPluginLoginResponse>
     */
    async login(context) {
        if (!context.chain) {
            throw new Error('The cleos wallet plugin requires a chain to be selected.');
        }
        if (!context.permissionLevel) {
            throw new Error('The cleos wallet plugin requires a permissionLevel to be specified.');
        }
        return {
            chain: context.chain.id,
            permissionLevel: context.permissionLevel,
        };
    }
    /**
     * Performs the wallet logic required to sign a transaction and return the signature.
     *
     * @param chain ChainDefinition
     * @param resolved ResolvedSigningRequest
     * @returns Promise<Signature>
     */
    async sign(resolved, context) {
        if (!context.ui) {
            throw new Error('The cleos wallet plugin requires a UI to be provided.');
        }
        // During the signing process, add a hook to display the cleos command to the user
        context.addHook(TransactHookTypes.afterSign, async () => {
            // Decode the transaction to be human readable
            const actions = [];
            for (const action of resolved.transaction.actions) {
                const abi = await context.abiCache.getAbi(action.account);
                actions.push({
                    ...action,
                    data: Serializer.objectify(action.decodeData(abi)),
                });
            }
            const decoded = {
                ...resolved.transaction,
                actions,
            };
            // Create the cleos command that will be used to sign the transaction
            const command = `cleos -u ${context.chain.url} push transaction '${JSON.stringify(decoded, null, 4)}'`;
            // Prompt the user with the command to sign the transaction
            await context.ui?.prompt({
                title: 'Sign with cleos',
                body: 'Copy the command to sign the transaction using cleos.',
                elements: [
                    {
                        type: 'textarea',
                        data: {
                            content: command,
                        },
                    },
                    {
                        type: 'button',
                        data: {
                            label: 'Copy to clipboard',
                            onClick: () => {
                                // Ensure the copy to clipboard will work
                                if (!navigator.clipboard || !window.isSecureContext) {
                                    alert('Copy to clipboard is not supported in this browser. Please copy the command manually.');
                                }
                                else {
                                    navigator.clipboard.writeText(command);
                                }
                            },
                        },
                    },
                ],
            });
        });
        // Return no signatures, as the cleos command will be used to sign the transaction
        return {
            signatures: [],
        };
    }
}

export { WalletPluginCleos };
//# sourceMappingURL=wallet-plugin-cleos.m.js.map
